import { useMemo, type RefObject } from 'react';
import { analyzeTranscript, PROSIGN_BUTTONS } from '@2x3/shared';
import { plural } from '../lib/format.js';

interface Props {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  textareaRef: RefObject<HTMLTextAreaElement>;
}

/**
 * The transcript box. It checks the copy against the corpus grammar as it is
 * typed, so a copier finds out about a stray colon now rather than from a
 * refused save, and it inserts the marks that are awkward to type.
 */
export default function TranscriptEditor({ value, onChange, disabled, textareaRef }: Props) {
  const analysis = useMemo(() => analyzeTranscript(value), [value]);
  const errors = analysis.issues.filter((i) => i.severity === 'error');
  const warnings = analysis.issues.filter((i) => i.severity === 'warning');

  /** Insert at the caret, with a space either side where one is needed. */
  const insert = (text: string, spaced: boolean): void => {
    const el = textareaRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const before = value.slice(0, start);
    const after = value.slice(end);
    const lead = spaced && before.length > 0 && !/\s$/.test(before) ? ' ' : '';
    const trail = spaced && !/^\s/.test(after) ? ' ' : '';
    const next = `${before}${lead}${text}${trail}${after}`;
    onChange(next);
    const caret = before.length + lead.length + text.length + trail.length;
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(caret, caret);
    });
  };

  /** [?]: wrap a single selected character, or open empty brackets at the caret. */
  const unsure = (): void => {
    const el = textareaRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    if (end - start === 1) {
      const next = `${value.slice(0, start)}[${value.slice(start, end)}]${value.slice(end)}`;
      onChange(next);
      requestAnimationFrame(() => {
        el.focus();
        el.setSelectionRange(end + 2, end + 2);
      });
      return;
    }
    const next = `${value.slice(0, start)}[]${value.slice(end)}`;
    onChange(next);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + 1, start + 1);
    });
  };

  const s = analysis.stats;

  return (
    <div className="transcript">
      <div className="toolbar" role="toolbar" aria-label="Insert a mark">
        <button type="button" disabled={disabled} onClick={() => insert('=', true)} title="BT, the break">
          = <span className="muted">BT</span>
        </button>
        {PROSIGN_BUTTONS.map((p) => (
          <button type="button" key={p} disabled={disabled} onClick={() => insert(`<${p}>`, true)}>
            &lt;{p}&gt;
          </button>
        ))}
        <span className="toolbar-gap" />
        <button
          type="button"
          disabled={disabled}
          onClick={unsure}
          title="Unsure of one character: select it (or type it inside) - W7[K]Z"
        >
          [?] <span className="muted">unsure</span>
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={() => insert('#', true)}
          title="A stretch you could not copy, any length"
        >
          # <span className="muted">can't copy</span>
        </button>
      </div>

      <textarea
        ref={textareaRef}
        className="transcript-box"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        spellCheck={false}
        autoCapitalize="characters"
        autoCorrect="off"
        autoComplete="off"
        rows={9}
        placeholder={'CQ CQ DE W1ABC W1ABC K\nW1ABC DE K2XYZ 5NN TX = NAME BOB <KN>'}
        aria-label="Your copy"
        aria-describedby="transcript-status"
      />

      <div id="transcript-status" className="transcript-status">
        <span className="muted">
          {value.trim() === ''
            ? 'Type what you hear. Letters are capitalised for you.'
            : `${plural(s.scored, 'character')} · ${plural(s.words, 'word')}` +
              (s.uncertain ? ` · ${s.uncertain} unsure` : '') +
              (s.gaps ? ` · ${plural(s.gaps, 'gap')}` : '')}
        </span>
      </div>

      {(errors.length > 0 || warnings.length > 0) && value.trim() !== '' && (
        <ul className="issues">
          {errors.map((i, n) => (
            <li key={`e${n}`} className="is-error">
              {i.snippet && <code>{i.snippet}</code>} {i.message}
            </li>
          ))}
          {warnings.map((i, n) => (
            <li key={`w${n}`} className="is-warning">
              {i.snippet && <code>{i.snippet}</code>} {i.message}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
